import mailApi from './mailApi';

// ============================================================================
// Intelligence — Smart Inbox views
// ============================================================================

export const getIntelligenceStats = async () => {
  const response = await mailApi.get('/api/mail/intelligence/stats');
  return response.data;
};

export const getAttentionQueue = async (limit = 50, offset = 0) => {
  const response = await mailApi.get('/api/mail/intelligence/attention-queue', {
    params: { limit, offset }
  });
  return response.data;
};

export const getAIHandled = async (limit = 50, offset = 0) => {
  const response = await mailApi.get('/api/mail/intelligence/ai-handled', {
    params: { limit, offset }
  });
  return response.data;
};

export const getAllEmails = async (limit = 50, offset = 0) => {
  const response = await mailApi.get('/api/mail/intelligence/all', {
    params: { limit, offset }
  });
  return response.data;
};

export const getEmailIntelligence = async (emailId) => {
  const response = await mailApi.get(`/api/mail/intelligence/email/${emailId}`);
  return response.data;
};

export const deleteEmail = async (emailId) => {
  const response = await mailApi.delete(`/api/mail/intelligence/email/${emailId}`);
  return response.data;
};

export const dismissEmail = async (emailId, body = {}) => {
  const response = await mailApi.post(`/api/mail/intelligence/dismiss/${emailId}`, body);
  return response.data;
};

export const undoDismiss = async (emailId) => {
  const response = await mailApi.post(`/api/mail/intelligence/undo-dismiss/${emailId}`);
  return response.data;
};

export const markReviewed = async (emailId) => {
  const response = await mailApi.post(`/api/mail/intelligence/mark-reviewed/${emailId}`);
  return response.data;
};

export const moveToAttention = async (emailId) => {
  const response = await mailApi.post(`/api/mail/intelligence/move-to-attention/${emailId}`);
  return response.data;
};

export const fileToAccounting = async (emailId) => {
  const response = await mailApi.post(`/api/mail/intelligence/file-to-accounting/${emailId}`);
  return response.data;
};

export const fileToCase = async (emailId, body) => {
  const response = await mailApi.post(`/api/mail/intelligence/file-to-case/${emailId}`, body);
  return response.data;
};

export const submitFeedback = async (emailId, body) => {
  const response = await mailApi.post(`/api/mail/intelligence/feedback/${emailId}`, body);
  return response.data;
};

export const bulkDelete = async (emailIds) => {
  const response = await mailApi.post('/api/mail/intelligence/bulk-delete', {
    email_ids: emailIds
  });
  return response.data;
};

// ============================================================================
// Sync
// ============================================================================

export const syncMail = async () => {
  const response = await mailApi.post('/api/mail/sync');
  return response.data;
};

// ============================================================================
// Classification / Auto-Filing
// ============================================================================

export const getClassificationSuggestions = async (params = {}) => {
  const response = await mailApi.get('/api/mail/classification/suggestions', { params });
  return response.data;
};

export const getRecentAutoFiled = async (days = 7, limit = 50) => {
  const response = await mailApi.get('/api/mail/classification/recent-auto-filed', {
    params: { days, limit }
  });
  return response.data;
};

export const approveClassification = async (emailId, data = {}) => {
  const response = await mailApi.post(`/api/mail/classification/${emailId}/approve`, data);
  return response.data;
};

export const rejectClassification = async (emailId, data = {}) => {
  const response = await mailApi.post(`/api/mail/classification/${emailId}/reject`, data);
  return response.data;
};

export const refileClassification = async (emailId, data) => {
  const response = await mailApi.post(`/api/mail/classification/${emailId}/refile`, data);
  return response.data;
};

export const getCaseClassifiedEmails = async (caseId, params = {}) => {
  const response = await mailApi.get(`/api/mail/classification/case/${caseId}/emails`, { params });
  return response.data;
};

export const getBriefingAttentionQueue = async (limit = 20) => {
  const response = await mailApi.get('/api/mail/classification/attention-queue', {
    params: { limit }
  });
  return response.data;
};

// ============================================================================
// Accounting Emails
// ============================================================================

export const getAccountingEmails = async (category = null, isProcessed = null, limit = 50) => {
  const params = { limit };
  if (category) params.category = category;
  if (isProcessed !== null) params.is_processed = isProcessed;
  const response = await mailApi.get('/api/email-filing/accounting', { params });
  return response.data;
};

export const getAccountingStats = async () => {
  const response = await mailApi.get('/api/email-filing/accounting/stats');
  return response.data;
};

export const markAccountingProcessed = async (accountingEmailId, journalEntryId = null, expenseId = null) => {
  const params = {};
  if (journalEntryId) params.journal_entry_id = journalEntryId;
  if (expenseId) params.expense_id = expenseId;
  const response = await mailApi.post(`/api/email-filing/accounting/${accountingEmailId}/process`, null, { params });
  return response.data;
};

// ============================================================================
// Cases (for filing modals)
// ============================================================================

export const searchMatters = async (query, limit = 20) => {
  const response = await mailApi.get('/api/virgo/cases', {
    params: { search: query, limit }
  });
  return response.data;
};

export const getAllCases = async (limit = 500) => {
  const response = await mailApi.get('/api/virgo/cases', { params: { limit } });
  return response.data;
};

// ============================================================================
// Search
// ============================================================================

export const searchEmails = async (query, params = {}) => {
  const response = await mailApi.get('/api/mail/search', { params: { q: query, ...params } });
  return response.data;
};

// ============================================================================
// Contact Lookup
// ============================================================================

export const lookupContactByEmail = async (email) => {
  const response = await mailApi.get('/api/contacts/lookup/by-email', { params: { email } });
  return response.data;
};

// ============================================================================
// Smart Features
// ============================================================================

export const getDailyBriefing = () => mailApi.get('/api/mail/smart/daily-briefing').then(r => r.data);

export const addDeadlineToCalendar = async (emailId, data) => {
  const response = await mailApi.post(`/api/mail/smart/deadlines/${emailId}/add-to-calendar`, data);
  return response.data;
};
